import type { TimesheetsSalaryWorkTime } from '@/modules/timesheets/types.graphql'
import { isMobile } from 'react-device-detect'
import type { ModuleConfigNode } from '.'
import type { SalaryGroupNode } from '@/modules/salary/types.graphql'

/**
 * Finds the SalaryWorkTimeNode for a user with the highest priority for a given module
 *
 * @param user UserNode containing user types
 * @param module ModuleConfigNode
 * @returns SalaryWorkTimeNode with the highest priority containing at least one of the users user types.
 */

export function getUsersHighestPrioritySalaryWorkTime(
  user: { salaryGroups: Pick<SalaryGroupNode, 'id'>[] } | undefined,
  module: ModuleConfigNode | null
): TimesheetsSalaryWorkTime | null {
  const salaryWorkTimes = [...(module?.salaryWorkTimes ?? [])]

  const usersUserTypeIds =
    user?.salaryGroups?.map(salaryGroup => salaryGroup.id) ?? ([] as string[])

  const filteredSalaryWorkTimes = salaryWorkTimes.filter(salaryWorkTime => {
    const salaryGroupIds =
      salaryWorkTime.salaryGroups?.edges?.map(edge => edge.node.id) ?? []
    return salaryGroupIds.some(id => usersUserTypeIds.includes(id))
  })

  if (filteredSalaryWorkTimes.length === 0) {
    const nonUserTypeSalaryWorkTimes = salaryWorkTimes.filter(
      salaryWorkTime => salaryWorkTime.salaryGroups?.edges.length === 0
    )
    if (nonUserTypeSalaryWorkTimes.length > 0) {
      return nonUserTypeSalaryWorkTimes.reduce((acc, cur) => {
        if (cur.priority < acc.priority) return cur
        return acc
      })
    } else {
      const first = salaryWorkTimes.sort((a, b) => a.priority - b.priority)[0]
      return first ?? null
    }
  }
  return filteredSalaryWorkTimes.reduce((acc, cur) => {
    if (cur.priority < acc.priority) return cur
    return acc
  })
}

type ExportGridSection = 'infringement'

export function responsiveExportGridAreas(
  hasUserField?: boolean,
  hasExportField = true,
  section?: ExportGridSection
) {
  const defaultDesktop = `
          'month month  exportOption'
          ${hasExportField ? "'exportType exportType exportFormat'" : ''}
          ${hasUserField ? `'user user user'` : ''}
        `

  if (!isMobile) {
    if (section && section === 'infringement') {
      return `
          'month month  exportOption'
          ${hasUserField ? `'user user exportFormat'` : ''}
        `
    } else {
      return defaultDesktop
    }
  }
  return `
        'month'
        'exportOption'
        ${hasExportField ? "'exportType'" : ''}
        ${hasExportField ? "'exportFormat'" : ''}
        ${hasUserField ? `'user'` : ''}
      `
}
